import axios from "axios";
import { API } from "../../../../config";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../../configureStore";
import {
  setCreateManagerCreationError,
  setCreateManagerCreationErrorShow,
  setCreateManagerValues,
} from "../../../slices/users/managersUsers";

import { generatePassword } from "../../locations/locationsAsyncActions";

export const userManagerCreation = createAsyncThunk(
  "CreateLocationReducer/userManagerCreation",
  async (
    arg: {
      token: string;
      username: string;
      password: string;
      associatedGeoPointId: number | null;
      role: number;
    },
    store
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    store.dispatch(setCreateManagerCreationErrorShow(false));
    store.dispatch(setCreateManagerCreationError([]));
    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${arg.token}`,
    };

    try {
      const response = await axios.post(
        `${API}/users/register`,
        {
          username: arg.username,
          password: arg.password,
          userType: "real",
          email: arg.username,
          remoteId: "",
          firstname: "",
          lastname: "",
          associatedGeoPointId: arg.associatedGeoPointId,
        },
        { headers }
      );

      const { data } = response;
      return data;
    } catch (error: any) {
      store.dispatch(setCreateManagerCreationErrorShow(true));
      store.dispatch(
        setCreateManagerCreationError(error?.response?.data?.message)
      );
      return store.rejectWithValue(error?.response?.data?.message);
    }
  }
);
export const userProfiles = createAsyncThunk(
  "CreateUsersReducer/userProfiles",
  async (token: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios.get(
        `${API}/users/user-facing-group-template?$eager=[name.[localizedTexts.locale], desc.[localizedTexts.locale]]&$limit=10000`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return store.fulfillWithValue(response.data.data);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);

export const checkGroupsInUser = async (
  token: string,
  userId: string
): Promise<any> => {
  const res = await axios({
    method: "GET",
    url: `${API}/users/organization-group-membership?$eager=[group]&userId=${userId}&deletedAt=null `,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  const filteringIds = data?.data
    ?.filter((group: any) => group.group.groupTemplateId !== null)
    .map((result: any) => result.group.groupTemplateId);

  return filteringIds;
};

export const findTheGroup = async (
  token: string,
  orgId: number,
  missingGroupTemplateId: number[]
): Promise<any> => {
  const queryString = missingGroupTemplateId
    .map((id) => `groupTemplateId[$in][]=${id}`)
    .join("&");
  const res = await axios({
    method: "GET",
    url: `${API}/users/organization-group?$eager=[groupTemplate]&orgId=${orgId}&${queryString}&deletedAt=null`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data;
};

export const createManagerRole = async (
  token: string,
  userId: string,
  groupId: number,
  membershipType: number
): Promise<any> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/organization-group-membership`,
    data: {
      userId,
      groupId,
      membershipType,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;
  return data;
};

export const createManagerGroup = async (
  token: string,
  orgName: string,
  templateName: string,
  username: string,
  defaultTypeId: number,
  orgId: number,
  missingGroupTemplateId: number
): Promise<any> => {
  const res = await axios({
    method: "POST",
    url: `${API}/users/organization-group`,
    data: {
      name: `${orgName} ${templateName} ${username}`,
      typeId: defaultTypeId,
      orgId,
      isAutoGenerated: true,
      groupTemplateId: missingGroupTemplateId,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const { data } = res;

  return data;
};

export const groupTemplateLooksLike = createAsyncThunk(
  "CreateUsersReducer/groupTemplateLooksLike",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/organization-group-template?$limit=10000`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return store.fulfillWithValue(response.data.data);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);

export const createMultiManagerUsersWithActions = createAsyncThunk(
  "CreateTablesUsersReducer/createMultiManagerUsersWithActions",
  async (args: any, store) => {
    const { navigate } = args;
    const {
      managerUsersConfigRows,
      createManagers,
      userRoles,
      groupTemplateRaw,
    } = (store.getState() as RootState).CreateManagersReducer;

    const { orgId, user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";
    store.dispatch(
      setCreateManagerValues({ ...createManagers, createLoading: true })
    );

    try {
      const usersPromises = managerUsersConfigRows.map((usersConfigs) => {
        return store.dispatch(
          userManagerCreation({
            token: token,
            username: usersConfigs.email,
            password: generatePassword(),
            associatedGeoPointId: usersConfigs?.assignedSite?.id ?? null,
            role: usersConfigs.role as number,
          })
        );
      });

      const createdUsers = await Promise.all(usersPromises);
      const res = createdUsers.map((v) => v.payload);

      store.dispatch(setCreateManagerValues(res));

      const userWithTableInformation = res.reduce((arr: any, e: any) => {
        arr.push(
          Object.assign(
            {},
            e, //current object
            {
              informationFromTable: managerUsersConfigRows.find(
                (a) => a.email === e.email
              ),
            }
          )
        );
        return arr;
      }, []);

      const arrayWithRoleSelected = userWithTableInformation.map(
        (user: any, index: number) => ({
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          username: user.username,
          userType: user.userType,
          email: user.email,
          remoteId: user.remoteId,
          firstname: user.firstname,
          lastname: user.lastname,
          id: user.id,
          emailVerifiedAt: user.emailVerifiedAt,
          lastAllotedDevice: user.lastAllotedDevice,
          deletedAt: user.deletedAt,
          isSuperAdmin: user.isSuperAdmin,
          attributeId: user.attributeId,
          associatedGeoPointId: user.associatedGeoPointId,
          metadata: user.metadata,
          group: user.group,
          roletypeId: user.informationFromTable.role,
          role: userRoles.filter((x: any) => {
            if (x.id === user.informationFromTable.role) {
              return x.groupTemplateIds;
            } else {
              return null;
            }
          })[0].groupTemplateIds,
          groupsSelected:
            user.informationFromTable.assignedGroups.length >= 1
              ? user.informationFromTable.groupsSelected
              : [],
          siteWithoutGroup: user.informationFromTable.assignedSite,
        })
      );

      if (res.length >= 1) {
        const checkGroupsPromises = arrayWithRoleSelected.map(
          (newManagerUsers: any) => {
            return checkGroupsInUser(token, newManagerUsers.id);
          }
        );

        const actualRoles = await Promise.all(checkGroupsPromises);
        const missingId = arrayWithRoleSelected.map((s: any) =>
          s.role.filter((s: number) => !actualRoles.flat().includes(s))
        );

        const findTheGroups = missingId.map((missingGroupTemplateId: any) => {
          return findTheGroup(token, orgId as number, missingGroupTemplateId);
        });

        const whatGroups = await Promise.all(findTheGroups);

        const toConvertInManager = arrayWithRoleSelected.reduce(
          (arr: any, e: any, index: number) => {
            arr.push(
              Object.assign(
                {},
                e, //current object
                {
                  assignedGroupViaIndex: whatGroups.find(
                    (a, i) =>
                      i === index &&
                      missingId
                        .find((a: number[], i: number) => i === index)
                        .flat()[0] === 28
                  )?.data[0],
                },
                {
                  isMissingId: missingId
                    .find((a: number[], i: number) => i === index)
                    .flat(),
                },
                {
                  groupTemplateLooksLike: groupTemplateRaw.filter(
                    (template) =>
                      template.id ===
                      missingId
                        .find((a: number[], i: number) => i === index)
                        .flat()[0]
                  ),
                }
              )
            );
            return arr;
          },
          []
        );
        const resultsPromises = toConvertInManager.map(async (info: any) => {
          // if user Selected backoffice manager
          if (info.assignedGroupViaIndex !== undefined) {
            return createManagerRole(
              token,
              info.id,
              info.assignedGroupViaIndex.id,
              info.assignedGroupViaIndex.groupTemplate.defaultMembershipType
            );
          } else {
            // if user Selected another kind of manager
            if (info.groupsSelected.length >= 1) {
              const managerRoles = info.groupsSelected.map((group: any) =>
                createManagerRole(
                  token,
                  info.id,
                  group.id,
                  groupTemplateRaw.find((f) => f.id === group.groupTemplateId)
                    ?.defaultMembershipType as number
                )
              );

              await Promise.all(managerRoles);
            }
          }
        });

        const done = await Promise.all(resultsPromises);
        store.dispatch(
          setCreateManagerValues({
            ...createManagers,
            createSuccess: createdUsers.length >= 1 ? true : false,
            createLoading: true,
            usersManagersCreated: createdUsers.length,
          })
        );

        setTimeout(() => navigate("/users"), 100);

        return done;
      }
    } catch (error: any) {
      console.error(error);
      store.dispatch(
        setCreateManagerValues({
          ...createManagers,
          createLoading: false,
          createError: error?.response?.data?.message,
        })
      );
    }
  }
);
